'use client';

import { forwardRef, ForwardRefRenderFunction, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { WidgetInstance, WidgetInstanceOptions } from 'friendly-challenge';
import { twJoin } from 'tailwind-merge';
import { LoadingSpinner } from '../../../core-components/LoadingSpinner';
import './style.css';
export type FriendlyCaptchaProps = {
  locale?: WidgetInstanceOptions['language'];
  onSuccess?: (solution: string) => void;
  onError?: (error: any) => void;
  onReady?: () => void;
  siteKey?: string;
  startMode?: WidgetInstanceOptions['startMode'];
};
export type FriendlyCaptchaRef = {
  reset: () => void;
  getSolution: () => string | undefined;
  challengeSolved: () => boolean;
  challengeFailed: () => boolean;
  wasChallenged: () => boolean;
  hasError: () => boolean;
  getError: () => any | undefined;
};
const FriendlyCaptchaRenderFunction: ForwardRefRenderFunction<FriendlyCaptchaRef, FriendlyCaptchaProps> = ({
  locale,
  onSuccess,
  onError,
  onReady,
  siteKey,
  startMode = 'focus'
}, ref) => {
  let [ready, setReady] = useState(false);
  let [success, setSuccess] = useState<boolean>();
  let [solution, setSolution] = useState<string>();
  let [error, setError] = useState<any>();
  const container = useRef<HTMLDivElement>(null);
  const widget = useRef<WidgetInstance | null>(null);
  let handleSuccess = useCallback((solution: string) => {
    setSuccess(true);
    setSolution(solution);
    if (onSuccess) {
      onSuccess(solution);
    }
  }, [onSuccess]);
  let handleError = useCallback((error: any) => {
    setSuccess(false);
    setError(error);
    if (onError) {
      onError(error);
    }
  }, [onError]);
  let handleReady = useCallback(() => {
    setReady(true);
    if (onReady) {
      onReady();
    }
  }, [onReady]);
  useImperativeHandle<FriendlyCaptchaRef, FriendlyCaptchaRef>(ref, () => {
    return {
      reset: () => {
        setSuccess(undefined);
        setError(undefined);
        widget.current?.reset();
      },
      getSolution: () => solution,
      challengeSolved: () => success === true,
      challengeFailed: () => success === false,
      wasChallenged: () => typeof success !== 'undefined',
      hasError: () => typeof error !== 'undefined',
      getError: () => error
    };
  });
  useEffect(() => {
    if (widget.current || !container.current) {
      return;
    }
    widget.current = new WidgetInstance(container.current, {
      startMode,
      language: locale,
      skipStyleInjection: true,
      puzzleEndpoint: 'https://eu-api.friendlycaptcha.eu/api/v1/puzzle',
      readyCallback: handleReady,
      doneCallback: handleSuccess,
      errorCallback: handleError,
      sitekey: siteKey
    });
    return () => widget.current?.reset();
  }, [container, locale, startMode, handleSuccess, handleError, handleReady, siteKey]);
  return <div className="max-w-[425px] relative look25" data-sentry-component="FriendlyCaptchaRenderFunction" data-sentry-source-file="FriendlyCaptcha.tsx">
      {!ready && <div className="frc-captcha">
          <div className="absolute inset-0 flex items-center justify-center">
            <LoadingSpinner size="md" />
          </div>
        </div>}
      <div ref={container} className={twJoin('frc-captcha', !ready && 'hidden')} />
    </div>;
};

/**
 * Don't forget to provide the `siteKey` & `locale` prop.
 *
 * Review the [documentation of `startMode` property](https://docs.friendlycaptcha.com/#/widget_api?id=data-start-attribute) to regulate when the user is challenged, the default for the component is 'focused'
 *
 * ## Example:
 *
 * ```tsx
 * let friendlyCaptcha = useRef<FriendlyCaptchaRef>();
 *
 * let handleSuccess = (solution: string) => {
 *  logger.debug('success', solution);
 * }
 *
 * let handleError = (error: any) => {
 *   logger.debug('Something went wrong');
 * }
 *
 * let handleSubmit = () => {
 *   // You can also access the friendly captcha state on demand:
 *
 *   if (friendlyCaptcha.current.getSolution()) {
 *     // The solution of a successful challenge
 *   }
 *
 *   if (friendlyCaptcha.current.challengeSolved()) {
 *     // Challenge successfully solved -- you can submit the form
 *   }
 *
 *   if (friendlyCaptcha.current.challengeFailed()) {
 *     // Challenge failed -- you should not submit the form
 *   }
 *
 *   if (friendlyCaptcha.current.wasChallenged()) {
 *     // The user was challenged
 *   }
 *
 *   if (friendlyCaptcha.current.hasError()) {
 *     // There was an error
 *   }
 *
 *   if (friendlyCaptcha.current.getError()) {
 *     // Returns the error object
 *   }
 *
 *   // Don't forget to reset the Captcha once you submit the data:
 *   friendlyCaptcha.current.reset();
 * }
 *
 * return (
 *   <div>
 *     <FriendlyCaptcha
 *       ref={friendlyCaptcha}
 *       onSuccess={handleSuccess}
 *       onError={handleError}
 *       locale="de"
 *       siteKey="<Your Site Key>"
 *     />
 *     <button onClick={handleSubmit}>Submit</button>
 *   </div>
 * );
 * ```
 */
export const FriendlyCaptcha = forwardRef<FriendlyCaptchaRef, FriendlyCaptchaProps>(FriendlyCaptchaRenderFunction);